/**
 * Heartbeat utility for real-time user validation and revocation
 * Combines smart polling with Google Admin API validation
 */

class HeartbeatManager {
  constructor(store, options = {}) {
    this.store = store
    this.heartbeatInterval = null
    this.isActive = true
    this.normalInterval = options.normalInterval || 2 * 60 * 1000  // 2 minutes when active
    this.backgroundInterval = options.backgroundInterval || 10 * 60 * 1000 // 10 minutes when tab hidden
    this.retryCount = 0
    this.maxRetries = 3
    this.apiEndpoint = options.apiEndpoint || '/.netlify/functions/heartbeat'
    this.mockHandler = options.mockHandler || null
    
    this.setupVisibilityHandlers()
  }

  /**
   * Start the smart heartbeat system
   */
  start() {
    if (!this.store.getters.isAuthenticated) {
      console.log('Heartbeat: User not authenticated, skipping')
      return
    }

    console.log('Heartbeat: Starting smart polling')
    this.setHeartbeatInterval(this.normalInterval)
    
    // Immediate check on start
    this.doHeartbeat()
  }

  /**
   * Stop the heartbeat system
   */
  stop() {
    console.log('Heartbeat: Stopping polling')
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  /**
   * Perform a single heartbeat check
   */
  async doHeartbeat() {
    if (!this.store.getters.isAuthenticated) {
      this.stop()
      return
    }

    // Check if we have the required user data
    const userEmail = this.store.getters.userEmail
    const userSub = this.store.getters.user?.sub
    const sessionToken = this.store.getters.sessionToken

    if (!userEmail || !userSub) {
      console.warn('Heartbeat: Missing user email or sub, skipping validation')
      return true
    }

    // Skip validation for test fixture users to prevent 500 errors
    if (this.isTestUser(userEmail)) {
      console.log('Heartbeat: Test user detected, skipping validation')
      return true
    }

    if (!sessionToken) {
      // Signed in before session tokens existed: the next lambda call will
      // 401 and force a fresh sign-in, so drop the stale state now.
      console.warn('Heartbeat: No session token, signing out')
      await this.handleRevocation('session_expired')
      return false
    }

    try {
      const payload = {
        email: userEmail,
        sub: userSub,
        timestamp: Date.now()
      }

      let response
      
      // Use mock handler if provided (for testing)
      if (this.mockHandler) {
        console.log('Heartbeat: Using mock handler for testing')
        response = await this.mockHandler(payload)
      } else {
        response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
          },
          body: JSON.stringify(payload)
        })
      }

      if (response.ok) {
        const data = await response.json().catch(() => ({}))
        if (data.sessionToken) {
          // Sliding session: each successful heartbeat carries a fresh token
          this.store.commit('SET_SESSION_TOKEN', data.sessionToken)
        }
        console.log('Heartbeat: User validated successfully')
        this.retryCount = 0 // Reset retry count on success
        return true
      } else if (response.status === 401) {
        // Session token expired or rejected: force a fresh sign-in
        console.log('Heartbeat: Session token rejected, signing out')
        await this.handleRevocation('session_expired')
        return false
      } else if (response.status === 403 || response.status === 404) {
        // User has been revoked/suspended or not found
        const errorData = await response.json().catch(() => ({}))
        console.log(`Heartbeat: User validation failed - ${response.status}: ${errorData.reason || 'unknown'}`)
        await this.handleRevocation()
        return false
      } else if (response.status >= 500) {
        // Server error - treat as temporary issue
        throw new Error(`Server error: HTTP ${response.status}`)
      } else {
        // Other client errors
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (error) {
      console.warn('Heartbeat: Validation failed', error.message)
      
      // Handle network failures gracefully
      this.retryCount++
      if (this.retryCount >= this.maxRetries) {
        console.log('Heartbeat: Max retries reached, checking token expiry')
        if (this.isTokenExpired()) {
          console.log('Heartbeat: Token appears expired, logging out')
          await this.handleRevocation()
          return false
        }
        // Reset retry count but don't logout (could be temporary network issue)
        console.log('Heartbeat: Continuing despite failures (likely network issue)')
        this.retryCount = 0
      }
      return true // Don't logout on network/server errors
    }
  }

  /**
   * Handle user revocation/suspension
   */
  async handleRevocation(reason = 'access_revoked') {
    console.log(`Heartbeat: Handling user revocation (${reason})`)
    this.stop()
    
    // Clear user data and redirect to login with reason
    await this.store.dispatch('logout')
    
    // Show notification or redirect with context
    if (window.location.pathname !== '/') {
      window.location.href = `/?reason=${reason}`
    }
  }

  /**
   * Check if current token appears to be expired
   */
  isTokenExpired() {
    const authAge = this.store.getters.authAge || 0
    const oneHour = 60 * 60 * 1000
    return authAge > oneHour
  }

  /**
   * Determine if this is a test fixture user (to prevent 500 errors during testing)
   */
  isTestUser(email) {
    if (!email) return false
    
    // Test fixture users have @test.westmoreland.com domain
    return email.includes('@test.westmoreland.com')
  }

  /**
   * Set up page visibility handlers for smart polling
   */
  setupVisibilityHandlers() {
    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        console.log('Heartbeat: Page hidden, reducing frequency')
        this.isActive = false
        this.setHeartbeatInterval(this.backgroundInterval)
      } else {
        console.log('Heartbeat: Page active, increasing frequency')
        this.isActive = true
        this.setHeartbeatInterval(this.normalInterval)
        // Immediate check when returning to tab
        this.doHeartbeat()
      }
    })

    // Handle page focus/blur as backup
    window.addEventListener('focus', () => {
      if (!this.isActive) {
        this.isActive = true
        this.setHeartbeatInterval(this.normalInterval)
        this.doHeartbeat()
      }
    })

    window.addEventListener('blur', () => {
      this.isActive = false
      this.setHeartbeatInterval(this.backgroundInterval)
    })

    // Handle page unload
    window.addEventListener('beforeunload', () => {
      this.stop()
    })
  }

  /**
   * Set the heartbeat interval
   */
  setHeartbeatInterval(interval) {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
    }
    
    this.heartbeatInterval = setInterval(() => {
      this.doHeartbeat()
    }, interval)
  }

  /**
   * Get current heartbeat status
   */
  getStatus() {
    return {
      active: !!this.heartbeatInterval,
      isPageActive: this.isActive,
      interval: this.isActive ? this.normalInterval : this.backgroundInterval,
      retryCount: this.retryCount,
      authenticated: this.store.getters.isAuthenticated
    }
  }
}

// Singleton instance
let heartbeatManager = null

/**
 * Initialize the heartbeat system
 * @param {Object} store - Vuex store instance
 * @param {Object} options - Configuration options for testing/customization
 */
export function initHeartbeat(store, options = {}) {
  if (heartbeatManager) {
    heartbeatManager.stop()
  }
  
  heartbeatManager = new HeartbeatManager(store, options)
  return heartbeatManager
}

/**
 * Start heartbeat monitoring
 */
export function startHeartbeat() {
  if (heartbeatManager) {
    heartbeatManager.start()
  } else {
    console.warn('Heartbeat: Manager not initialized')
  }
}

/**
 * Stop heartbeat monitoring
 */
export function stopHeartbeat() {
  if (heartbeatManager) {
    heartbeatManager.stop()
  }
}

/**
 * Perform a manual heartbeat check
 */
export async function performHeartbeat() {
  if (heartbeatManager) {
    return heartbeatManager.doHeartbeat()
  }
  return false
}

/**
 * Get heartbeat status for debugging
 */
export function getHeartbeatStatus() {
  if (heartbeatManager) {
    return heartbeatManager.getStatus()
  }
  return { active: false }
}

export default {
  initHeartbeat,
  startHeartbeat,
  stopHeartbeat,
  performHeartbeat,
  getHeartbeatStatus
}